import React, { useCallback, useEffect, useState } from "react";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Container,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	FormControlLabel,
	IconButton,
	Paper,
	Snackbar,
	Stack,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import { Briefcase, Pencil, Plus, Trash2 } from "lucide-react";
import { supabase } from "../../config/supabase";

const TABLE_NAME = "open_positions";

const emptyForm = {
	title: "",
	tags: "",
	apply_email: "",
	is_active: true,
	sort_order: 0,
};

const parseTags = (value) =>
	String(value || "")
		.split(",")
		.map((t) => t.trim())
		.filter(Boolean);

const OpenPositions = () => {
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [deleteOpen, setDeleteOpen] = useState(false);
	const [editing, setEditing] = useState(null);
	const [form, setForm] = useState(emptyForm);
	const [snackbar, setSnackbar] = useState({
		open: false,
		message: "",
		severity: "success",
	});

	const showMsg = (message, severity = "success") =>
		setSnackbar({ open: true, message, severity });

	const fetchRows = useCallback(async () => {
		try {
			const { data, error } = await supabase
				.from(TABLE_NAME)
				.select("*")
				.order("sort_order", { ascending: true })
				.order("created_at", { ascending: true });
			if (error) throw error;
			setRows(data || []);
		} catch (err) {
			console.error("Failed to load open positions:", err);
			showMsg(err.message || "Failed to load open positions", "error");
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchRows();
	}, [fetchRows]);

	const openCreate = () => {
		setEditing(null);
		setForm({ ...emptyForm, sort_order: rows.length });
		setDialogOpen(true);
	};

	const openEdit = (row) => {
		setEditing(row);
		setForm({
			title: row.title || "",
			tags: (row.tags || []).join(", "),
			apply_email: row.apply_email || "",
			is_active: row.is_active !== false,
			sort_order: row.sort_order ?? 0,
		});
		setDialogOpen(true);
	};

	const handleSave = async () => {
		if (!form.title.trim()) {
			showMsg("Title is required", "warning");
			return;
		}
		setSaving(true);
		try {
			const payload = {
				title: form.title.trim(),
				tags: parseTags(form.tags),
				apply_email: (form.apply_email || "").trim() || null,
				is_active: !!form.is_active,
				sort_order: Number.isFinite(Number(form.sort_order))
					? Number(form.sort_order)
					: 0,
			};

			if (editing) {
				const { error } = await supabase
					.from(TABLE_NAME)
					.update(payload)
					.eq("id", editing.id);
				if (error) throw error;
				showMsg("Position updated");
			} else {
				const { error } = await supabase.from(TABLE_NAME).insert(payload);
				if (error) throw error;
				showMsg("Position added");
			}

			setDialogOpen(false);
			setLoading(true);
			await fetchRows();
		} catch (err) {
			console.error("Save failed:", err);
			showMsg(err.message || "Failed to save position", "error");
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async () => {
		if (!editing) return;
		setSaving(true);
		try {
			const { error } = await supabase
				.from(TABLE_NAME)
				.delete()
				.eq("id", editing.id);
			if (error) throw error;
			showMsg("Position deleted");
			setDeleteOpen(false);
			setEditing(null);
			setLoading(true);
			await fetchRows();
		} catch (err) {
			console.error("Delete failed:", err);
			showMsg(err.message || "Failed to delete position", "error");
		} finally {
			setSaving(false);
		}
	};

	return (
		<Container maxWidth="lg" sx={{ py: 4 }}>
			<Stack
				direction={{ xs: "column", sm: "row" }}
				alignItems={{ xs: "flex-start", sm: "center" }}
				justifyContent="space-between"
				spacing={2}
				sx={{ mb: 1 }}>
				<Stack direction="row" alignItems="center" spacing={1.5}>
					<Briefcase size={28} />
					<Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
						Open Positions
					</Typography>
				</Stack>
				<Button
					variant="contained"
					startIcon={<Plus size={16} />}
					onClick={openCreate}>
					Add position
				</Button>
			</Stack>
			<Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
				Manage careers listings on the Who we are page. Inactive roles stay
				hidden on the public site. Tags are comma-separated (first tag is
				highlighted).
			</Typography>

			{loading ? (
				<Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
					<CircularProgress />
				</Box>
			) : (
				<Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
					{rows.length === 0 ? (
						<Box sx={{ p: 4, textAlign: "center" }}>
							<Typography color="text.secondary">
								No positions yet. Add the first open role.
							</Typography>
						</Box>
					) : (
						<TableContainer>
							<Table>
								<TableHead>
									<TableRow>
										<TableCell>Order</TableCell>
										<TableCell>Title</TableCell>
										<TableCell>Tags</TableCell>
										<TableCell>Active</TableCell>
										<TableCell align="right">Actions</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{rows.map((row) => (
										<TableRow key={row.id}>
											<TableCell>{row.sort_order}</TableCell>
											<TableCell>{row.title}</TableCell>
											<TableCell>
												{(row.tags || []).join(", ") || "—"}
											</TableCell>
											<TableCell>{row.is_active ? "Yes" : "No"}</TableCell>
											<TableCell align="right">
												<IconButton
													aria-label="Edit"
													onClick={() => openEdit(row)}>
													<Pencil size={16} />
												</IconButton>
												<IconButton
													color="error"
													aria-label="Delete"
													onClick={() => {
														setEditing(row);
														setDeleteOpen(true);
													}}>
													<Trash2 size={16} />
												</IconButton>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					)}
				</Paper>
			)}

			<Dialog
				open={dialogOpen}
				onClose={() => !saving && setDialogOpen(false)}
				fullWidth
				maxWidth="sm">
				<DialogTitle>
					{editing ? "Edit position" : "Add position"}
				</DialogTitle>
				<DialogContent>
					<Stack spacing={2} sx={{ mt: 1 }}>
						<TextField
							label="Title"
							value={form.title}
							onChange={(e) =>
								setForm((prev) => ({ ...prev, title: e.target.value }))
							}
							fullWidth
							required
						/>
						<TextField
							label="Tags"
							value={form.tags}
							onChange={(e) =>
								setForm((prev) => ({ ...prev, tags: e.target.value }))
							}
							helperText="Comma-separated, e.g. Full-time, Kerala, Field role"
							fullWidth
						/>
						<TextField
							label="Apply email (optional)"
							value={form.apply_email}
							onChange={(e) =>
								setForm((prev) => ({ ...prev, apply_email: e.target.value }))
							}
							helperText="Leave blank to use the website contact email."
							fullWidth
						/>
						<TextField
							label="Display order"
							type="number"
							value={form.sort_order}
							onChange={(e) =>
								setForm((prev) => ({ ...prev, sort_order: e.target.value }))
							}
							fullWidth
						/>
						<FormControlLabel
							control={
								<Switch
									checked={!!form.is_active}
									onChange={(e) =>
										setForm((prev) => ({
											...prev,
											is_active: e.target.checked,
										}))
									}
								/>
							}
							label="Active on website"
						/>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDialogOpen(false)} disabled={saving}>
						Cancel
					</Button>
					<Button
						variant="contained"
						onClick={handleSave}
						disabled={saving || !form.title.trim()}>
						{saving ? "Saving..." : "Save"}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={deleteOpen}
				onClose={() => !saving && setDeleteOpen(false)}>
				<DialogTitle>Delete position?</DialogTitle>
				<DialogContent>
					<DialogContentText>
						This removes <strong>{editing?.title}</strong> from the website.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDeleteOpen(false)} disabled={saving}>
						Cancel
					</Button>
					<Button
						color="error"
						variant="contained"
						onClick={handleDelete}
						disabled={saving}>
						{saving ? "Deleting..." : "Delete"}
					</Button>
				</DialogActions>
			</Dialog>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={5000}
				onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
				<Alert
					severity={snackbar.severity}
					onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
					sx={{ width: "100%" }}>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Container>
	);
};

export default OpenPositions;
